import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #fff;
  padding: 120px 2rem 4rem;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  color: #111;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 3px solid ${theme.colors.primary};
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: #111;
  margin-bottom: 1rem;
`;

const SubTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: #222;
  margin: 1.25rem 0 0.5rem;
`;

const Text = styled.p`
  font-family: 'Montserrat', sans-serif;
  font-size: 0.93rem;
  color: #333;
  line-height: 1.8;
  margin-bottom: 0.75rem;
`;

const TextUpper = styled(Text)`
  text-transform: uppercase;
  font-size: 0.88rem;
`;

const Link = styled.a`
  color: ${theme.colors.primary};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const List = styled.ul`
  font-family: 'Montserrat', sans-serif;
  font-size: 0.93rem;
  color: #333;
  line-height: 1.8;
  padding-left: 1.5rem;
  margin-bottom: 0.75rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eee;
  margin: 2rem 0;
`;

const Datenschutz = () => {
  return (
    <>
      <Helmet>
        <title>Datenschutzerklärung – Optik Geissler</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <PageWrapper>
        <Container>
          <PageTitle>Datenschutzerklärung</PageTitle>

          {/* ── 1. DATENSCHUTZ AUF EINEN BLICK ─────────────────────────── */}
          <Section>
            <SectionTitle>1. Datenschutz auf einen Blick</SectionTitle>

            <SubTitle>Allgemeine Hinweise</SubTitle>
            <Text>
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren
              personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene
              Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
              Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem
              Text aufgeführten Datenschutzerklärung.
            </Text>

            <SubTitle>Datenerfassung auf dieser Website</SubTitle>
            <Text><strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong></Text>
            <Text>
              Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen
              Kontaktdaten können Sie dem Abschnitt „Hinweis zur Verantwortlichen Stelle" in dieser
              Datenschutzerklärung entnehmen.
            </Text>

            <Text><strong>Wie erfassen wir Ihre Daten?</strong></Text>
            <Text>
              Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann
              es sich z.&nbsp;B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
            </Text>
            <Text>
              Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website
              durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z.&nbsp;B.
              Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs). Die Erfassung dieser
              Daten erfolgt automatisch, sobald Sie diese Website betreten.
            </Text>

            <Text><strong>Wofür nutzen wir Ihre Daten?</strong></Text>
            <Text>
              Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu
              gewährleisten. Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
              Sofern über die Website Verträge geschlossen oder angebahnt werden können, werden die
              übermittelten Daten auch für Vertragsangebote, Bestellungen oder sonstige
              Auftragsanfragen verarbeitet.
            </Text>

            <Text><strong>Welche Rechte haben Sie bezüglich Ihrer Daten?</strong></Text>
            <Text>
              Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und
              Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein
              Recht, die Berichtigung oder Löschung dieser Daten zu verlangen. Wenn Sie eine
              Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese Einwilligung jederzeit
              für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter bestimmten Umständen
              die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Des
              Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.
            </Text>
            <Text>Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit an uns wenden.</Text>
          </Section>

          {/* ── 2. HOSTING ──────────────────────────────────────────────── */}
          <Section>
            <SectionTitle>2. Hosting</SectionTitle>
            <Text>Wir hosten die Inhalte unserer Website bei folgendem Anbieter:</Text>

            <SubTitle>All-Inkl</SubTitle>
            <Text>
              Anbieter ist die ALL-INKL.COM – Neue Medien Münnich, Inh. René Münnich, Hauptstraße 68,
              02742 Friedersdorf (nachfolgend All-Inkl). Details entnehmen Sie der
              Datenschutzerklärung von All-Inkl:{' '}
              <Link href="https://all-inkl.com/datenschutzinformationen/" target="_blank" rel="noopener noreferrer">
                https://all-inkl.com/datenschutzinformationen/
              </Link>.
            </Text>
            <Text>
              Die Verwendung von All-Inkl erfolgt auf Grundlage von Art.&nbsp;6 Abs.&nbsp;1
              lit.&nbsp;f DSGVO. Wir haben ein berechtigtes Interesse an einer möglichst zuverlässigen
              Darstellung unserer Website. Sofern eine entsprechende Einwilligung abgefragt wurde,
              erfolgt die Verarbeitung ausschließlich auf Grundlage von Art.&nbsp;6 Abs.&nbsp;1
              lit.&nbsp;a DSGVO und § 25 Abs.&nbsp;1 TDDDG, soweit die Einwilligung die Speicherung
              von Cookies oder den Zugriff auf Informationen im Endgerät des Nutzers (z.&nbsp;B.
              Device-Fingerprinting) im Sinne des TDDDG umfasst. Die Einwilligung ist jederzeit
              widerrufbar.
            </Text>

            <SubTitle>Auftragsverarbeitung</SubTitle>
            <Text>
              Wir haben einen Vertrag über Auftragsverarbeitung (AVV) zur Nutzung des oben genannten
              Dienstes geschlossen. Hierbei handelt es sich um einen datenschutzrechtlich
              vorgeschriebenen Vertrag, der gewährleistet, dass dieser die personenbezogenen Daten
              unserer Websitebesucher nur nach unseren Weisungen und unter Einhaltung der DSGVO
              verarbeitet.
            </Text>
          </Section>

          {/* ── 3. ALLGEMEINE HINWEISE ──────────────────────────────────── */}
          <Section>
            <SectionTitle>3. Allgemeine Hinweise und Pflichtinformationen</SectionTitle>

            <SubTitle>Datenschutz</SubTitle>
            <Text>
              Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir
              behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen
              Datenschutzvorschriften sowie dieser Datenschutzerklärung.
            </Text>
            <Text>
              Wenn Sie diese Website benutzen, werden verschiedene personenbezogene Daten erhoben.
              Personenbezogene Daten sind Daten, mit denen Sie persönlich identifiziert werden können.
              Die vorliegende Datenschutzerklärung erläutert, welche Daten wir erheben und wofür wir
              sie nutzen. Sie erläutert auch, wie und zu welchem Zweck das geschieht.
            </Text>
            <Text>
              Wir weisen darauf hin, dass die Datenübertragung im Internet (z.&nbsp;B. bei der
              Kommunikation per E-Mail) Sicherheitslücken aufweisen kann. Ein lückenloser Schutz der
              Daten vor dem Zugriff durch Dritte ist nicht möglich.
            </Text>

            <SubTitle>Hinweis zur verantwortlichen Stelle</SubTitle>
            <Text>Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:</Text>
            <Text>
              Christian Geissler<br />
              Hauptplatz 5, 8490 Bad Radkersburg<br />
              Telefon: <Link href="tel:+4334762036">+43 3476 200 36</Link><br />
              E-Mail: <Link href="mailto:radkersburg@optik-geissler.at">radkersburg@optik-geissler.at</Link>
            </Text>
            <Text>
              Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder
              gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung von
              personenbezogenen Daten (z.&nbsp;B. Namen, E-Mail-Adressen o.&nbsp;Ä.) entscheidet.
            </Text>

            <SubTitle>Speicherdauer</SubTitle>
            <Text>
              Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer genannt
              wurde, verbleiben Ihre personenbezogenen Daten bei uns, bis der Zweck für die
              Datenverarbeitung entfällt. Wenn Sie ein berechtigtes Löschersuchen geltend machen oder
              eine Einwilligung zur Datenverarbeitung widerrufen, werden Ihre Daten gelöscht, sofern
              wir keine anderen rechtlich zulässigen Gründe für die Speicherung Ihrer
              personenbezogenen Daten haben (z.&nbsp;B. steuer- oder handelsrechtliche
              Aufbewahrungsfristen); im letztgenannten Fall erfolgt die Löschung nach Fortfall dieser
              Gründe.
            </Text>

            <SubTitle>Allgemeine Hinweise zu den Rechtsgrundlagen der Datenverarbeitung auf dieser Website</SubTitle>
            <Text>
              Sofern Sie in die Datenverarbeitung eingewilligt haben, verarbeiten wir Ihre
              personenbezogenen Daten auf Grundlage von Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;a DSGVO
              bzw. Art.&nbsp;9 Abs.&nbsp;2 lit.&nbsp;a DSGVO, sofern besondere Datenkategorien nach
              Art.&nbsp;9 Abs.&nbsp;1 DSGVO verarbeitet werden. Im Falle einer ausdrücklichen
              Einwilligung in die Übertragung personenbezogener Daten in Drittstaaten erfolgt die
              Datenverarbeitung außerdem auf Grundlage von Art.&nbsp;49 Abs.&nbsp;1 lit.&nbsp;a DSGVO.
              Sofern Sie in die Speicherung von Cookies oder in den Zugriff auf Informationen in Ihr
              Endgerät (z.&nbsp;B. via Device-Fingerprinting) eingewilligt haben, erfolgt die
              Datenverarbeitung zusätzlich auf Grundlage von § 25 Abs.&nbsp;1 TDDDG. Die Einwilligung
              ist jederzeit widerrufbar. Sind Ihre Daten zur Vertragserfüllung oder zur Durchführung
              vorvertraglicher Maßnahmen erforderlich, verarbeiten wir Ihre Daten auf Grundlage des
              Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;b DSGVO. Des Weiteren verarbeiten wir Ihre Daten,
              sofern diese zur Erfüllung einer rechtlichen Verpflichtung erforderlich sind auf
              Grundlage von Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;c DSGVO. Die Datenverarbeitung kann
              ferner auf Grundlage unseres berechtigten Interesses nach Art.&nbsp;6 Abs.&nbsp;1
              lit.&nbsp;f DSGVO erfolgen. Über die jeweils im Einzelfall einschlägigen
              Rechtsgrundlagen wird in den folgenden Absätzen dieser Datenschutzerklärung informiert.
            </Text>

            <SubTitle>Empfänger von personenbezogenen Daten</SubTitle>
            <Text>
              Im Rahmen unserer Geschäftstätigkeit arbeiten wir mit verschiedenen externen Stellen
              zusammen. Dabei ist teilweise auch eine Übermittlung von personenbezogenen Daten an
              diese externen Stellen erforderlich. Wir geben personenbezogene Daten nur dann an
              externe Stellen weiter, wenn dies im Rahmen einer Vertragserfüllung erforderlich ist,
              wenn wir gesetzlich hierzu verpflichtet sind (z.&nbsp;B. Weitergabe von Daten an
              Steuerbehörden), wenn wir ein berechtigtes Interesse nach Art.&nbsp;6 Abs.&nbsp;1
              lit.&nbsp;f DSGVO an der Weitergabe haben oder wenn eine sonstige Rechtsgrundlage die
              Datenweitergabe erlaubt. Beim Einsatz von Auftragsverarbeitern geben wir
              personenbezogene Daten unserer Kunden nur auf Grundlage eines gültigen Vertrags über
              Auftragsverarbeitung weiter. Im Falle einer gemeinsamen Verarbeitung wird ein Vertrag
              über gemeinsame Verarbeitung geschlossen.
            </Text>

            <SubTitle>Widerruf Ihrer Einwilligung zur Datenverarbeitung</SubTitle>
            <Text>
              Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich.
              Sie können eine bereits erteilte Einwilligung jederzeit widerrufen. Die Rechtmäßigkeit
              der bis zum Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf unberührt.
            </Text>

            <SubTitle>Widerspruchsrecht gegen die Datenerhebung in besonderen Fällen sowie gegen Direktwerbung (Art. 21 DSGVO)</SubTitle>
            <TextUpper>
              Wenn die Datenverarbeitung auf Grundlage von Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;e oder
              f DSGVO erfolgt, haben Sie jederzeit das Recht, aus Gründen, die sich aus Ihrer
              besonderen Situation ergeben, gegen die Verarbeitung Ihrer personenbezogenen Daten
              Widerspruch einzulegen; dies gilt auch für ein auf diese Bestimmungen gestütztes
              Profiling. Die jeweilige Rechtsgrundlage, auf denen eine Verarbeitung beruht, entnehmen
              Sie dieser Datenschutzerklärung. Wenn Sie Widerspruch einlegen, werden wir Ihre
              betroffenen personenbezogenen Daten nicht mehr verarbeiten, es sei denn, wir können
              zwingende schutzwürdige Gründe für die Verarbeitung nachweisen, die Ihre Interessen,
              Rechte und Freiheiten überwiegen oder die Verarbeitung dient der Geltendmachung,
              Ausübung oder Verteidigung von Rechtsansprüchen (Widerspruch nach Art.&nbsp;21
              Abs.&nbsp;1 DSGVO).
            </TextUpper>
            <TextUpper>
              Werden Ihre personenbezogenen Daten verarbeitet, um Direktwerbung zu betreiben, so
              haben Sie das Recht, jederzeit Widerspruch gegen die Verarbeitung Sie betreffender
              personenbezogener Daten zum Zwecke derartiger Werbung einzulegen; dies gilt auch für
              das Profiling, soweit es mit solcher Direktwerbung in Verbindung steht. Wenn Sie
              widersprechen, werden Ihre personenbezogenen Daten anschließend nicht mehr zum Zwecke
              der Direktwerbung verwendet (Widerspruch nach Art.&nbsp;21 Abs.&nbsp;2 DSGVO).
            </TextUpper>

            <SubTitle>Beschwerderecht bei der zuständigen Aufsichtsbehörde</SubTitle>
            <Text>
              Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht bei
              einer Aufsichtsbehörde, insbesondere in dem Mitgliedstaat ihres gewöhnlichen
              Aufenthalts, ihres Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes zu. Das
              Beschwerderecht besteht unbeschadet anderweitiger verwaltungsrechtlicher oder
              gerichtlicher Rechtsbehelfe.
            </Text>
            <Text>
              Zuständige Aufsichtsbehörde für Österreich ist die Datenschutzbehörde (DSB),
              Barichgasse 40–42, 1030 Wien,{' '}
              <Link href="https://www.dsb.gv.at" target="_blank" rel="noopener noreferrer">
                www.dsb.gv.at
              </Link>.
            </Text>

            <SubTitle>Recht auf Datenübertragbarkeit</SubTitle>
            <Text>
              Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in Erfüllung
              eines Vertrags automatisiert verarbeiten, an sich oder an einen Dritten in einem
              gängigen, maschinenlesbaren Format aushändigen zu lassen. Sofern Sie die direkte
              Übertragung der Daten an einen anderen Verantwortlichen verlangen, erfolgt dies nur,
              soweit es technisch machbar ist.
            </Text>

            <SubTitle>Auskunft, Berichtigung und Löschung</SubTitle>
            <Text>
              Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf
              unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten, deren
              Herkunft und Empfänger und den Zweck der Datenverarbeitung und ggf. ein Recht auf
              Berichtigung oder Löschung dieser Daten. Hierzu sowie zu weiteren Fragen zum Thema
              personenbezogene Daten können Sie sich jederzeit an uns wenden.
            </Text>

            <SubTitle>Recht auf Einschränkung der Verarbeitung</SubTitle>
            <Text>
              Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten
              zu verlangen. Hierzu können Sie sich jederzeit an uns wenden. Das Recht auf
              Einschränkung der Verarbeitung besteht in folgenden Fällen:
            </Text>
            <List>
              <li>
                Wenn Sie die Richtigkeit Ihrer bei uns gespeicherten personenbezogenen Daten
                bestreiten, benötigen wir in der Regel Zeit, um dies zu überprüfen. Für die Dauer der
                Prüfung haben Sie das Recht, die Einschränkung der Verarbeitung Ihrer
                personenbezogenen Daten zu verlangen.
              </li>
              <li>
                Wenn die Verarbeitung Ihrer personenbezogenen Daten unrechtmäßig
                geschah/geschieht, können Sie statt der Löschung die Einschränkung der
                Datenverarbeitung verlangen.
              </li>
              <li>
                Wenn wir Ihre personenbezogenen Daten nicht mehr benötigen, Sie sie jedoch zur
                Ausübung, Verteidigung oder Geltendmachung von Rechtsansprüchen benötigen, haben
                Sie das Recht, statt der Löschung die Einschränkung der Verarbeitung Ihrer
                personenbezogenen Daten zu verlangen.
              </li>
              <li>
                Wenn Sie einen Widerspruch nach Art.&nbsp;21 Abs.&nbsp;1 DSGVO eingelegt haben,
                muss eine Abwägung zwischen Ihren und unseren Interessen vorgenommen werden.
                Solange noch nicht feststeht, wessen Interessen überwiegen, haben Sie das Recht,
                die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen.
              </li>
            </List>
            <Text>
              Wenn Sie die Verarbeitung Ihrer personenbezogenen Daten eingeschränkt haben, dürfen
              diese Daten – von ihrer Speicherung abgesehen – nur mit Ihrer Einwilligung oder zur
              Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen oder zum Schutz der
              Rechte einer anderen natürlichen oder juristischen Person oder aus Gründen eines
              wichtigen öffentlichen Interesses der Europäischen Union oder eines Mitgliedstaats
              verarbeitet werden.
            </Text>
          </Section>

          {/* ── 4. DATENERFASSUNG AUF DIESER WEBSITE ───────────────────── */}
          <Section>
            <SectionTitle>4. Datenerfassung auf dieser Website</SectionTitle>

            <SubTitle>Kontaktformular</SubTitle>
            <Text>
              Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem
              Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung
              der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben
              wir nicht ohne Ihre Einwilligung weiter.
            </Text>
            <Text>
              Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art.&nbsp;6 Abs.&nbsp;1
              lit.&nbsp;b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt
              oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen
              Fällen beruht die Verarbeitung auf unserem berechtigten Interesse an der effektiven
              Bearbeitung der an uns gerichteten Anfragen (Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;f DSGVO)
              oder auf Ihrer Einwilligung (Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;a DSGVO) sofern diese
              abgefragt wurde; die Einwilligung ist jederzeit widerrufbar.
            </Text>
            <Text>
              Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie uns zur
              Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für
              die Datenspeicherung entfällt (z.&nbsp;B. nach abgeschlossener Bearbeitung Ihrer
              Anfrage). Zwingende gesetzliche Bestimmungen – insbesondere Aufbewahrungsfristen –
              bleiben unberührt.
            </Text>

            {/* ── FORMSPREE (site-specific addition) ── */}
            <SubTitle>Auftragsverarbeiter: Formspree</SubTitle>
            <Text>
              Zur technischen Übermittlung der Kontaktformular-Daten nutzen wir den Dienst{' '}
              <strong>Formspree, Inc.</strong>, 2261 Market Street #5039, San Francisco, CA 94114,
              USA. Die von Ihnen im Formular eingegebenen Daten (Name, E-Mail-Adresse oder
              Telefonnummer, Nachricht, gewählter Standort) werden beim Absenden direkt an die Server
              von Formspree übertragen und von dort an uns weitergeleitet.
            </Text>
            <Text>
              Formspree verarbeitet die Daten ausschließlich zur Weiterleitung der Anfrage an uns und
              handelt dabei als Auftragsverarbeiter auf Grundlage eines Auftragsverarbeitungsvertrags
              (Data Processing Agreement). Da Formspree seinen Sitz in den USA hat, kann es zu einer
              Übermittlung personenbezogener Daten in die USA kommen. Formspree nutzt als geeignete
              Garantie Standardvertragsklauseln (SCCs) gemäß Art.&nbsp;46 Abs.&nbsp;2 lit.&nbsp;c
              DSGVO.
            </Text>
            <Text>
              Rechtsgrundlage der Verarbeitung ist Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;a DSGVO (Ihre
              Einwilligung durch Aktivierung der Zustimmungs-Checkbox vor dem Absenden) sowie
              Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;b DSGVO (Vertragsanbahnung). Ihre Einwilligung können
              Sie jederzeit mit Wirkung für die Zukunft widerrufen, indem Sie uns per E-Mail
              kontaktieren.
            </Text>
            <Text>
              Weitere Informationen zur Datenschutzerklärung von Formspree:{' '}
              <Link href="https://formspree.io/legal/privacy-policy" target="_blank" rel="noopener noreferrer">
                formspree.io/legal/privacy-policy
              </Link>
            </Text>

            <Divider />

            <SubTitle>Anfrage per E-Mail, Telefon oder Telefax</SubTitle>
            <Text>
              Wenn Sie uns per E-Mail, Telefon oder Telefax kontaktieren, wird Ihre Anfrage inklusive
              aller daraus hervorgehenden personenbezogenen Daten (Name, Anfrage) zum Zwecke der
              Bearbeitung Ihres Anliegens bei uns gespeichert und verarbeitet. Diese Daten geben wir
              nicht ohne Ihre Einwilligung weiter.
            </Text>
            <Text>
              Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art.&nbsp;6 Abs.&nbsp;1
              lit.&nbsp;b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt
              oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen
              Fällen beruht die Verarbeitung auf unserem berechtigten Interesse an der effektiven
              Bearbeitung der an uns gerichteten Anfragen (Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;f DSGVO)
              oder auf Ihrer Einwilligung (Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;a DSGVO) sofern diese
              abgefragt wurde; die Einwilligung ist jederzeit widerrufbar.
            </Text>
            <Text>
              Die von Ihnen an uns per Kontaktanfragen übersandten Daten verbleiben bei uns, bis Sie
              uns zur Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck
              für die Datenspeicherung entfällt (z.&nbsp;B. nach abgeschlossener Bearbeitung Ihres
              Anliegens). Zwingende gesetzliche Bestimmungen – insbesondere gesetzliche
              Aufbewahrungsfristen – bleiben unberührt.
            </Text>
          </Section>

          {/* ── 5. EINGEBETTETE INHALTE DRITTER ─────────────────────────── */}
          <Section>
            <SectionTitle>5. Eingebettete Inhalte Dritter</SectionTitle>

            <SubTitle>Virtueller Rundgang (3dbranchen.at)</SubTitle>
            <Text>
              Auf unserer Seite „Über uns" bieten wir einen virtuellen 360°-Rundgang unseres
              Standorts Mureck an, der vom Anbieter <strong>3dbranchen.at</strong> bereitgestellt
              wird. Dieser Inhalt wird erst dann geladen, wenn Sie aktiv auf die Schaltfläche
              „Virtuellen Rundgang starten" klicken und damit Ihre Einwilligung erteilen
              (Zwei-Klick-Lösung).
            </Text>
            <Text>
              Mit dem Laden des Inhalts stellt Ihr Browser eine Verbindung zu den Servern von
              3dbranchen.at her. Dabei werden Daten – insbesondere Ihre IP-Adresse, Browsertyp,
              Betriebssystem sowie die aufgerufene URL – an 3dbranchen.at übertragen. Auf die
              Verarbeitung dieser Daten durch 3dbranchen.at haben wir keinen Einfluss.
            </Text>
            <Text>
              Rechtsgrundlage ist Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;a DSGVO (Ihre aktive
              Einwilligung durch Klick auf die Schaltfläche). Sie können Ihre Einwilligung jederzeit
              widerrufen, indem Sie die Seite neu laden – der Inhalt wird dann nicht erneut
              automatisch geladen.
            </Text>
          </Section>

          {/* ── 6. SCHRIFTARTEN ─────────────────────────────────────────── */}
          <Section>
            <SectionTitle>6. Schriftarten</SectionTitle>
            <Text>
              Diese Website verwendet die Schriftarten <strong>Poppins</strong> und{' '}
              <strong>Montserrat</strong>. Die Schriftartdateien sind vollständig lokal auf unserem
              Webserver gespeichert und werden ausschließlich von dort ausgeliefert (Self-Hosting via
              npm-Paket <em>@fontsource</em>). Es findet <strong>keine Verbindung zu externen
              Servern</strong> – insbesondere nicht zu Google Fonts – statt. Es werden dabei keine
              personenbezogenen Daten an Dritte übermittelt.
            </Text>
          </Section>

          {/* ── 7. EXTERNE LINKS ────────────────────────────────────────── */}
          <Section>
            <SectionTitle>7. Externe Links</SectionTitle>
            <Text>
              Unsere Website enthält einen Link zur Facebook-Seite von Optik Geissler (betrieben von
              Meta Platforms Ireland Limited, 4 Grand Canal Square, Dublin 2, Irland). Beim Anklicken
              dieses Links verlassen Sie unsere Website; ab diesem Zeitpunkt gilt die
              Datenschutzerklärung von Meta. Wir haben auf die dortige Datenverarbeitung keinen
              Einfluss und empfehlen, die{' '}
              <Link href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">
                Datenschutzerklärung von Meta
              </Link>{' '}
              zu lesen, bevor Sie den Link aufrufen.
            </Text>
            <Text>
              Auf unserer Website sind ausschließlich einfache Verlinkungen eingebunden. Es ist kein
              Facebook-Pixel, kein Like-Button und kein sonstiges Social-Plugin aktiv, das beim
              Besuch unserer Seite Daten an Meta übermittelt.
            </Text>
          </Section>

          <Text style={{ color: '#aaa', fontSize: '0.8rem', marginTop: '2rem' }}>
            Quelle Grundtext: <Link href="https://www.e-recht24.de" target="_blank" rel="noopener noreferrer">e-recht24.de</Link> | Ergänzungen: Formspree, 3dbranchen.at, Schriftarten, Externe Links
          </Text>
        </Container>
      </PageWrapper>
    </>
  );
};

export default Datenschutz;
